import React, { createContext, useContext, useState } from 'react';

interface LanguageContextType {
  language: string;
  setLanguage: (lang: string) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

interface LanguageProviderProps {
  children: React.ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState('ne');

  const setLanguage = (lang: string) => {
    setLanguageState(lang);
    localStorage.setItem('language', lang);
  };

  const t = (key: string): string => {
    // Simple translation function - in a real app, you'd use i18next
    const translations: Record<string, Record<string, string>> = {
      en: {
        'nav.home': 'Home',
        'nav.dashboard': 'Dashboard',
        'nav.grievances': 'Grievances',
        'nav.login': 'Login',
        'nav.logout': 'Logout',
        'nav.reports': 'Report and Analysis',
        'nav.settings': 'Site Setting',
        'nav.users': 'Ministry Management',
        'common.loading': 'Loading...',
        'nav.profile': 'User Profile',
        'common.save': 'Save',
        'common.cancel': 'Cancel',
        'common.delete': 'Delete',
        'common.edit': 'Edit',
        'common.submit': 'Submit',
        'grievances.title': 'Grievances',
        'grievances.submit': 'Submit Grievance',
        'grievances.track': 'Track Grievance',
        'grievances.statistic': 'Statistic',
        'auth.login': 'Login',
        'auth.logout': 'Logout',
        'auth.email': 'Email',
        'auth.password': 'Password',

      },
      ne: {
        'nav.home': 'गृहपुष्ठ',
        'nav.dashboard': 'ड्यासबोड',
        'nav.grievances': 'गुनासोहरू',
        'nav.login': 'लगइन',
        'nav.logout': 'लगआउट',
        'nav.reports': 'रिर्पोट/डाटा',
        'nav.settings': 'साइट सेटिङ्ग',
        'nav.users': 'मन्त्रालय व्यवस्थापन',
        'nav.profile': 'युजर प्रोफाईल',
        'common.loading': 'लोड हुँदै...',
        'common.save': 'सुरक्षित गर्नुहोस्',
        'common.cancel': 'रद्द गर्नुहोस्',
        'common.delete': 'मेटाउनुहोस्',
        'common.edit': 'सम्पादन गर्नुहोस्',
        'common.submit': 'पेश गर्नुहोस्',
        'grievances.title': 'गुनासोहरू',
        'grievances.submit': 'गुनासो पेश गर्नुहोस्',
        'grievances.track': 'गुनासो ट्र्याक गर्नुहोस्',
        'grievances.statistic': 'तथ्यांक विवरण',
        'auth.login': 'लगइन',
        'auth.logout': 'लगआउट',
        'auth.email': 'इमेल',
        'auth.password': 'पासवर्ड',
      },
    };

    return translations[language]?.[key] || key;
  };

  const value: LanguageContextType = {
    language,
    setLanguage,
    t,
  };

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};
